import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Button,
  IconButton,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import {
  Close,
  Delete,
  Star,
} from '@mui/icons-material';

const ProgramComparison = ({
  programIds,
  programs,
  onClose,
  onRemoveProgram,
  onViewDetails,
  onApplyNow
}) => {
  const comparePrograms = programs.filter(program => programIds.includes(program.id));

  if (comparePrograms.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          专业对比
        </Typography>
        <Typography variant="body2" color="text.secondary">
          请选择要对比的专业
        </Typography>
      </Box>
    );
  }

  const formatTuition = (tuition) => {
    if (!tuition || typeof tuition === 'string') {
      return tuition || '-';
    }
    if (tuition.international && typeof tuition.international === 'number') {
      return `RM ${tuition.international.toLocaleString()}`;
    }
    return '-';
  };

  const formatAmount = (amount) => {
    if (amount && typeof amount === 'number') {
      return amount.toLocaleString();
    }
    return '0';
  };

  const getLevelColor = (level) => {
    const colors = {
      '学士': 'success',
      '硕士': 'primary',
      '博士': 'secondary',
      '文凭': 'info'
    };
    return colors[level] || 'default';
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 头部 */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        p: 3, 
        borderBottom: 1, 
        borderColor: 'divider' 
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          专业对比 ({comparePrograms.length})
        </Typography>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Box>

      {/* 对比内容 */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
        {/* 移动端卡片视图 */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Stack spacing={3}>
            {comparePrograms.map((program) => (
              <Card key={program.id} variant="outlined">
                <CardContent>
                  {/* 移除按钮 */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <IconButton 
                      size="small" 
                      onClick={() => onRemoveProgram(program.id)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Box>

                  {/* 大学信息 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      src={program.university.logo}
                      sx={{ width: 32, height: 32, mr: 2 }}
                    >
                      {program.university.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2">
                        {program.university.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        排名 #{program.university.ranking}
                      </Typography>
                    </Box>
                  </Box>

                  {/* 专业名称 */}
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    {program.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {program.nameEn}
                  </Typography>

                  {/* 基本信息 */}
                  <Stack spacing={1} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">类别：</Typography>
                      <Chip label={program.category} size="small" variant="outlined" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">级别：</Typography>
                      <Chip 
                        label={program.level} 
                        size="small" 
                        color={getLevelColor(program.level)}
                        variant="outlined" 
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">学制：</Typography>
                      <Typography variant="body2">{program.duration}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">语言：</Typography>
                      <Typography variant="body2">{program.language}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">学费：</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatTuition(program.tuitionFee)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">奖学金：</Typography>
                      <Typography variant="body2">
                        {program.scholarship ? `有 (RM ${formatAmount(program.scholarshipAmount)})` : '无'}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* 操作按钮 */}
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={() => onViewDetails(program.id)}
                    >
                      查看详情
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      onClick={() => onApplyNow(program.id)}
                    >
                      立即申请
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>

        {/* 桌面端表格视图 */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>对比项目</TableCell>
                  {comparePrograms.map((program) => (
                    <TableCell key={program.id} align="center" sx={{ minWidth: 250 }}>
                      <Box>
                        <IconButton 
                          size="small" 
                          onClick={() => onRemoveProgram(program.id)}
                          sx={{ float: 'right', color: 'error.main' }}
                        >
                          <Delete />
                        </IconButton>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Avatar
                            src={program.university.logo}
                            sx={{ width: 24, height: 24, mr: 1 }}
                          >
                            {program.university.name.charAt(0)}
                          </Avatar>
                          <Typography variant="caption" color="text.secondary">
                            {program.university.name}
                          </Typography>
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {program.name}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* 英文名称 */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>英文名称</TableCell>
                  {comparePrograms.map((program) => (
                    <TableCell key={program.id} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {program.nameEn}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>

                {/* 专业类别 */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>专业类别</TableCell>
                  {comparePrograms.map((program) => (
                    <TableCell key={program.id} align="center">
                      <Chip label={program.category} size="small" variant="outlined" />
                    </TableCell>
                  ))}
                </TableRow>

                {/* 学位级别 */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>学位级别</TableCell>
                  {comparePrograms.map((program) => (
                    <TableCell key={program.id} align="center">
                      <Chip 
                        label={program.level} 
                        size="small" 
                        color={getLevelColor(program.level)}
                        variant="outlined" 
                      />
                    </TableCell>
                  ))}
                </TableRow>

                {/* 学制 */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>学制</TableCell>
                  {comparePrograms.map((program) => (
                    <TableCell key={program.id} align="center">
                      {program.duration}
                    </TableCell>
                  ))}
                </TableRow>

                {/* 授课语言 */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>授课语言</TableCell>
                  {comparePrograms.map((program) => (
                    <TableCell key={program.id} align="center">
                      {program.language}
                    </TableCell>
                  ))}
                </TableRow>

                {/* 学费 */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>学费 (国际生)</TableCell>
                  {comparePrograms.map((program) => (
                    <TableCell key={program.id} align="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatTuition(program.tuitionFee)} /年
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>

                {/* 奖学金 */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>奖学金</TableCell>
                  {comparePrograms.map((program) => (
                    <TableCell key={program.id} align="center">
                      {program.scholarship ? (
                        <Box>
                          <Chip label="有" size="small" color="success" />
                          {program.scholarshipAmount && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                              RM {formatAmount(program.scholarshipAmount)}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Chip label="无" size="small" color="default" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* 大学排名 */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>大学排名</TableCell>
                  {comparePrograms.map((program) => (
                    <TableCell key={program.id} align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Star sx={{ fontSize: 16, color: 'warning.main', mr: 0.5 }} />
                        <Typography variant="body2">
                          #{program.university.ranking}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>

                {/* 入学时间 */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>入学时间</TableCell>
                  {comparePrograms.map((program) => (
                    <TableCell key={program.id} align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                        {program.intakeMonths.map((month, index) => (
                          <Chip
                            key={index}
                            label={month}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                  ))}
                </TableRow>

                {/* 操作按钮 */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>操作</TableCell>
                  {comparePrograms.map((program) => (
                    <TableCell key={program.id} align="center">
                      <Stack spacing={1}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => onViewDetails(program.id)}
                        >
                          查看详情
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => onApplyNow(program.id)}
                        >
                          立即申请
                        </Button>
                      </Stack>
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default ProgramComparison;
